import { useEffect, useState, useRef } from 'react'
import pick from 'lodash/pick'
import { functions } from '@utils/firebase'
import {
  getOrgMembers,
  getOrgInvitedMembers,
  updateOrganization,
} from '@services/OrganizationsService'
import { getStripeConnectAccount } from '@services/StripeService'
import {
  Member,
  OrgMembersData,
  InviteRequest,
  OrgMemberProfile,
} from '@Interfaces/Data/OrganizationInterface'
import { StripeConnectAccount } from '@Interfaces/Data/StripeInterface'
import { UseOrganizationMembersProps } from '@Interfaces/Hooks/DataInterface'

const MEMBER_PICK_KEYS: (keyof Member)[] = [
  'active',
  'email',
  'hideTips',
  'postNewOpportunities',
  'publicPosts',
  'position',
  'volunteerApplicationReview',
  'volunteerCheckIn',
  'invite',
  'receiveNewApplications',
  'receivePostComments',
  'role',
  'verified',
  'viewDonations',
  'firstName',
  'lastName',
  'jobTitle',
  'stripeCustomerId',
]

const profileToMember = (u: OrgMemberProfile): Member => ({
  ...pick(u, MEMBER_PICK_KEYS),
  id: u.id ?? '',
})

const inviteToMember = (invite: InviteRequest): Member => ({
  ...invite,
  invite: true,
})

const authUserRowsFromCallable = (res: unknown): Array<{ uid?: string; email?: string }> => {
  if (!res || typeof res !== 'object' || !('data' in res)) return []
  const { data } = res as { data: unknown }
  return Array.isArray(data) ? data : []
}

export const useOrganizationMembers = ({
  selectedOrg,
  currentUser,
  onNotAloud,
}: UseOrganizationMembersProps) => {
  const [members, setMembers] = useState<Member[]>([])
  const [users, setUsers] = useState<Member[] | null>(null)
  const [invitedMembers, setInvitedMembers] = useState<Member[]>([])
  const [owner, setOwner] = useState(false)
  const [userType, setUserType] = useState('')
  const [currentMember, setCurrentMember] = useState<Member | null>(null)
  const [sponsors, setSponsors] = useState<Member[]>([])
  const [canMakeActions, setCanMakeActions] = useState(false)
  const [loading, setLoading] = useState(true)
  const [stripeConnectAccount, setStripeConnectAccount] = useState<StripeConnectAccount | null>(
    null
  )
  const lastMembersHashRef = useRef<string>('')
  const getAuthUsersTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!selectedOrg) {
      setMembers([])
      setOwner(false)
      setUserType('')
      setSponsors([])
      setCanMakeActions(false)
      return
    }
    if (!currentUser) {
      return
    }

    setOwner(selectedOrg.owner === currentUser.id)
    setLoading(true)

    const memberSub = getOrgMembers(
      currentUser,
      selectedOrg,
      (data: OrgMembersData) => {
        setMembers(data.members.map(profileToMember))
        setSponsors(data.sponsors.map(profileToMember))
        if (data.userType) {
          setUserType(data.userType)
        }
        if (data.currentMember) {
          setCurrentMember(profileToMember(data.currentMember))
        }
        setLoading(false)
      },
      undefined,
      onNotAloud
    )
    const inviteSub = getOrgInvitedMembers(selectedOrg, (invites: InviteRequest[]) => {
      setInvitedMembers(invites.map(inviteToMember))
    })

    if (selectedOrg.stripeID) {
      getStripeConnectAccount(selectedOrg).then((response) => {
        if (response.success && response.data) {
          setStripeConnectAccount(response.data)
        }
      })
    } else {
      setStripeConnectAccount(null)
    }

    return () => {
      memberSub()
      inviteSub()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg?.id, currentUser?.id])

  useEffect(() => {
    if (!stripeConnectAccount || !selectedOrg || selectedOrg?.stripeID !== stripeConnectAccount?.id)
      return

    if (stripeConnectAccount?.charges_enabled && !selectedOrg?.stripeComplete) {
      updateOrganization(selectedOrg, { stripeComplete: true }, [], [])
    } else if (!stripeConnectAccount?.charges_enabled && selectedOrg?.stripeComplete) {
      updateOrganization(selectedOrg, { stripeComplete: false }, [], [])
    }
  }, [stripeConnectAccount?.id, selectedOrg?.id, selectedOrg?.stripeComplete])

  useEffect(() => {
    if (!selectedOrg || members.length === 0) {
      setUsers(null)
      return
    }

    const membersHash = members
      .map((x) => x.id)
      .sort()
      .join(',')

    if (lastMembersHashRef.current === membersHash) return
    lastMembersHashRef.current = membersHash

    if (getAuthUsersTimeoutRef.current) {
      clearTimeout(getAuthUsersTimeoutRef.current)
    }

    getAuthUsersTimeoutRef.current = setTimeout(() => {
      const membersMutate = members.map((x) => ({ uid: x.id }))
      const stringifiedMembers = JSON.stringify(membersMutate)

      functions
        .httpsCallable('getAuthUsersV2')(stringifiedMembers)
        .then((res: unknown) => {
          const payload = authUserRowsFromCallable(res)
          const temp: Member[] = []
          members.forEach((member) => {
            const row = payload.find((x) => x.uid === member.id)
            if (row) member.email = row.email
            temp.push(member)
          })
          setUsers(temp)
        })
    }, 100)

    return () => {
      if (getAuthUsersTimeoutRef.current) {
        clearTimeout(getAuthUsersTimeoutRef.current)
      }
    }
  }, [members.length, selectedOrg?.id])

  return {
    members,
    setMembers,
    users,
    setUsers,
    invitedMembers,
    owner,
    userType,
    currentMember,
    sponsors,
    canMakeActions,
    loading,
    setLoading,
    stripeConnectAccount,
  }
}
